import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";
import { listListAnalyses } from "@/api/lookups";
import type {
  ListAnalysisMode,
  ListAnalysisStatus,
  ListAnalysisSummary,
} from "@/api/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DataTable } from "@/components/table/DataTable";
import { ListAnalysisStatusBadge } from "@/components/analysis/ListAnalysisHistory";

/**
 * Раздел «Анализы» (TM-4c1fb82b) — реестр всех доступных анализов по спискам
 * встреч (фича admin-only). Таблица DataTable с колонками Дата · Тип · Объект ·
 * Кто · Название · Статус + строка клиентских фильтров под шапкой. Клик по
 * строке ведёт на страницу разбора /analyses/$analysisId.
 */

const POLL_INTERVAL_MS = 30_000;
// Клиентская фильтрация по загруженным строкам — тянем с запасом.
const REGISTRY_LIMIT = 200;

function hasActiveStatus(rows: ListAnalysisSummary[]): boolean {
  return rows.some((r) => r.status === "pending" || r.status === "running");
}

/** «Объект» анализа: сотрудник (manager) / клиент (client) / — (free). */
function objectDisplay(row: ListAnalysisSummary): string {
  if (row.mode === "manager") return row.employee_display ?? "—";
  if (row.mode === "client") return row.client_display ?? "—";
  return "—";
}

function titleDisplay(row: ListAnalysisSummary): string {
  return row.title ?? row.query_text;
}

interface Filters {
  since: string;
  until: string;
  mode: "" | ListAnalysisMode;
  object: string;
  author: string;
  title: string;
  status: "" | ListAnalysisStatus;
}

const EMPTY_FILTERS: Filters = {
  since: "",
  until: "",
  mode: "",
  object: "",
  author: "",
  title: "",
  status: "",
};

const MODES: ListAnalysisMode[] = ["free", "manager", "client"];
const STATUSES: ListAnalysisStatus[] = ["pending", "running", "done", "error"];

function includesCI(haystack: string | null, needle: string): boolean {
  if (!needle.trim()) return true;
  return (haystack ?? "").toLowerCase().includes(needle.trim().toLowerCase());
}

function applyFilters(
  rows: ListAnalysisSummary[],
  f: Filters,
): ListAnalysisSummary[] {
  return rows.filter((r) => {
    const day = r.created_at.slice(0, 10);
    if (f.since && day < f.since) return false;
    if (f.until && day > f.until) return false;
    if (f.mode && r.mode !== f.mode) return false;
    if (f.status && r.status !== f.status) return false;
    if (!includesCI(objectDisplay(r), f.object)) return false;
    if (!includesCI(r.author_display, f.author)) return false;
    if (!includesCI(titleDisplay(r), f.title)) return false;
    return true;
  });
}

export function AnalysesView() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);

  const analysesQ = useQuery({
    queryKey: ["list-analyses", { registry: true }],
    queryFn: () => listListAnalyses({ limit: REGISTRY_LIMIT }),
    refetchInterval: (q) =>
      q.state.data !== undefined && hasActiveStatus(q.state.data)
        ? POLL_INTERVAL_MS
        : false,
  });

  const rows = useMemo(
    () => applyFilters(analysesQ.data ?? [], filters),
    [analysesQ.data, filters],
  );
  const fmtDate = (iso: string): string =>
    new Date(iso).toLocaleString(i18n.language);

  const columns = useMemo<ColumnDef<ListAnalysisSummary, unknown>[]>(
    () => [
      {
        id: "created_at",
        header: t("listAnalysis.registry.columns.date"),
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-muted-foreground">
            {fmtDate(row.original.created_at)}
          </span>
        ),
      },
      {
        id: "mode",
        header: t("listAnalysis.registry.columns.type"),
        cell: ({ row }) => t(`listAnalysis.mode.${row.original.mode}`),
      },
      {
        id: "object",
        header: t("listAnalysis.registry.columns.object"),
        cell: ({ row }) => objectDisplay(row.original),
      },
      {
        id: "author",
        header: t("listAnalysis.registry.columns.author"),
        cell: ({ row }) => row.original.author_display ?? t("common.dash"),
      },
      {
        id: "title",
        header: t("listAnalysis.registry.columns.title"),
        cell: ({ row }) => (
          <span className="font-medium">{titleDisplay(row.original)}</span>
        ),
      },
      {
        id: "status",
        header: t("listAnalysis.registry.columns.status"),
        cell: ({ row }) => (
          <ListAnalysisStatusBadge
            status={row.original.status}
            testId={`list-analysis-registry-status-${row.original.id}`}
          />
        ),
      },
    ],
    // fmtDate closes over i18n.language; t is stable per language.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language],
  );

  return (
    <div className="space-y-4" data-testid="analyses-view">
      <header>
        <h1 className="text-xl font-semibold">
          {t("listAnalysis.page.title")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t("listAnalysis.page.subtitle")}
        </p>
      </header>

      <AnalysesFilters filters={filters} onChange={setFilters} />

      <DataTable
        data={rows}
        columns={columns}
        testId="analyses-registry-table"
        isLoading={analysesQ.isLoading}
        emptyMessage={t("listAnalysis.section.empty")}
        onRowClick={(row) =>
          void navigate({
            to: "/analyses/$analysisId",
            params: { analysisId: row.id },
          })
        }
      />
    </div>
  );
}

interface AnalysesFiltersProps {
  filters: Filters;
  onChange: (next: Filters) => void;
}

/** Строка клиентских фильтров под шапкой реестра. */
function AnalysesFilters({ filters, onChange }: AnalysesFiltersProps) {
  const { t } = useTranslation();
  const set = (patch: Partial<Filters>): void =>
    onChange({ ...filters, ...patch });
  return (
    <div
      className="grid grid-cols-1 gap-2 md:grid-cols-3 xl:grid-cols-7"
      data-testid="analyses-filters"
    >
      <Input
        type="date"
        aria-label={t("listAnalysis.registry.filters.dateFrom")}
        value={filters.since}
        onChange={(e) => set({ since: e.target.value })}
      />
      <Input
        type="date"
        aria-label={t("listAnalysis.registry.filters.dateTo")}
        value={filters.until}
        onChange={(e) => set({ until: e.target.value })}
      />
      <select
        aria-label={t("listAnalysis.registry.filters.type")}
        className="h-9 rounded-md border bg-background px-2 text-sm"
        value={filters.mode}
        onChange={(e) => set({ mode: e.target.value as Filters["mode"] })}
        data-testid="analyses-filter-type"
      >
        <option value="">{t("listAnalysis.registry.filters.anyType")}</option>
        {MODES.map((m) => (
          <option key={m} value={m}>
            {t(`listAnalysis.mode.${m}`)}
          </option>
        ))}
      </select>
      <Input
        type="text"
        aria-label={t("listAnalysis.registry.filters.object")}
        placeholder={t("listAnalysis.registry.columns.object")}
        value={filters.object}
        onChange={(e) => set({ object: e.target.value })}
        data-testid="analyses-filter-object"
      />
      <Input
        type="text"
        aria-label={t("listAnalysis.registry.filters.author")}
        placeholder={t("listAnalysis.registry.columns.author")}
        value={filters.author}
        onChange={(e) => set({ author: e.target.value })}
        data-testid="analyses-filter-author"
      />
      <Input
        type="text"
        aria-label={t("listAnalysis.registry.filters.title")}
        placeholder={t("listAnalysis.registry.columns.title")}
        value={filters.title}
        onChange={(e) => set({ title: e.target.value })}
        data-testid="analyses-filter-title"
      />
      <div className="flex gap-2">
        <select
          aria-label={t("listAnalysis.registry.filters.status")}
          className="h-9 flex-1 rounded-md border bg-background px-2 text-sm"
          value={filters.status}
          onChange={(e) => set({ status: e.target.value as Filters["status"] })}
          data-testid="analyses-filter-status"
        >
          <option value="">
            {t("listAnalysis.registry.filters.anyStatus")}
          </option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(`listAnalysis.status.${s}`)}
            </option>
          ))}
        </select>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => onChange(EMPTY_FILTERS)}
          data-testid="analyses-filter-reset"
        >
          {t("listAnalysis.registry.filters.reset")}
        </Button>
      </div>
    </div>
  );
}
